import { useId, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type {
  ConversationActorPolicy,
  ConversationPresentation,
  FormField,
  HumanRequest,
  JsonObject,
  Problem,
} from '../api/types';
import {
  coerceFieldValue,
  fieldType,
  groupIssuesByKey,
  issuesFromProblem,
  problemMetaText,
  validateSubset,
  type FieldIssue,
} from './schema';

export interface StandardFormProps {
  request: HumanRequest;
  /** 回答CASに使う。TaskRequest.version。 */
  expectedVersion: number;
  /** 送信中・awaiting_answer以外はfalse。 */
  canAnswer: boolean;
  submitting: boolean;
  /** サーバーから返ったproblem。409/422の表示に使う。 */
  problem: Problem | null;
  onSubmit(answer: Record<string, unknown>): void;
  /** 409のときの「最新を取得」導線。 */
  onReload(): void;
}

/** widgetが保持する生の入力値。text/textarea/number/selectは文字列、checkboxはboolean。 */
export type WidgetValue = string | boolean;

export function StandardForm(props: StandardFormProps): ReactElement {
  const { request, expectedVersion, canAnswer, submitting, problem, onSubmit, onReload } = props;
  const [inputs, setInputs] = useState<Record<string, WidgetValue>>({});
  const [precheckIssues, setPrecheckIssues] = useState<FieldIssue[]>([]);
  const idPrefix = useId();

  const serverIssues = problem !== null && problem.status === 422 ? issuesFromProblem(problem) : [];
  const issues = groupIssuesByKey([...precheckIssues, ...serverIssues]);
  const disabled = !canAnswer || submitting;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const answer = buildFieldValues(request.form.fields, inputs, request.answerSchema);
    const found = validateSubset(request.answerSchema, answer);
    setPrecheckIssues(found);
    if (found.length === 0) onSubmit(answer);
  }

  return (
    <section className="standard-form">
      <h3 className="standard-form-title">{request.title}</h3>
      <p className="standard-form-description">{request.description}</p>

      <ViewPanel view={request.view} />
      {request.conversation !== undefined && (
        <ConversationPreview conversation={request.conversation} />
      )}

      <form className="form" onSubmit={handleSubmit} noValidate>
        {request.form.fields.map((field) => (
          <FieldRow
            key={field.key}
            field={field}
            id={`${idPrefix}-${field.key}`}
            value={inputs[field.key] ?? (field.widget === 'checkbox' ? false : '')}
            messages={issues.get(field.key) ?? []}
            disabled={disabled}
            onChange={(next) => setInputs((prev) => ({ ...prev, [field.key]: next }))}
          />
        ))}

        <AnswerProblemNotice
          problem={problem}
          expectedVersion={expectedVersion}
          unkeyedMessages={issues.get('') ?? []}
          onReload={onReload}
        />

        <button type="submit" className="form-submit" disabled={disabled}>
          {submitting ? '送信中…' : '回答する'}
        </button>
        <p className="form-note">
          この画面の検査は入力補助です。回答の可否はサーバー側の検証が正本です。
        </p>
      </form>
    </section>
  );
}

/** number widgetへ渡す属性。開始入力フォームがschemaのminimum/maximumから作る。 */
export interface NumberRange {
  min?: number;
  max?: number;
  step: number | 'any';
}

export interface FieldRowProps {
  field: FormField;
  id: string;
  value: WidgetValue;
  messages: string[];
  disabled: boolean;
  onChange(next: WidgetValue): void;
  /*
   * ここから下は開始入力フォーム（StartRunPanel）だけが渡す。
   * HumanTaskの回答フォームは渡さないので、描画はこれまでと変わらない。
   */
  /** 必須の印を出し、aria-requiredを付ける。 */
  required?: boolean;
  /** schemaのdescription。ラベルの下に補足として出す。 */
  description?: string;
  range?: NumberRange;
  onBlur?(): void;
}

export function FieldRow({
  field,
  id,
  value,
  messages,
  disabled,
  onChange,
  required,
  description,
  range,
  onBlur,
}: FieldRowProps): ReactElement {
  const errorId = `${id}-error`;
  const invalid = messages.length > 0;
  const control: ControlAttributes = {
    id,
    disabled,
    className: 'field-input',
    'aria-invalid': invalid,
    'aria-describedby': invalid ? errorId : undefined,
    'aria-required': required === true ? true : undefined,
    onBlur,
  };

  return (
    <div className={invalid ? 'field field-invalid' : 'field'}>
      <label className="field-label" htmlFor={id}>
        {field.label}
        {/* 読み上げはaria-requiredが担うので、見た目の印は重ねて読ませない。 */}
        {required === true && (
          <span className="field-required" aria-hidden="true">
            必須
          </span>
        )}
      </label>
      {description !== undefined && <p className="field-description">{description}</p>}
      {renderWidget(field, value, control, onChange, range)}
      {messages.map((message) => (
        <p className="field-error" id={errorId} key={message}>
          {message}
        </p>
      ))}
    </div>
  );
}

interface ControlAttributes {
  id: string;
  disabled: boolean;
  className: string;
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
  'aria-required': true | undefined;
  onBlur: (() => void) | undefined;
}

function renderWidget(
  field: FormField,
  value: WidgetValue,
  control: ControlAttributes,
  onChange: (next: WidgetValue) => void,
  range: NumberRange | undefined,
): ReactElement {
  switch (field.widget) {
    case 'textarea':
      return (
        <textarea
          {...control}
          value={String(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'number':
      return (
        <input
          {...control}
          type="number"
          min={range?.min}
          max={range?.max}
          step={range?.step}
          value={String(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'checkbox':
      return (
        <input
          {...control}
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
      );
    case 'select':
      return (
        <select
          {...control}
          value={String(value)}
          onChange={(event) => onChange(event.target.value)}
        >
          {/* option値はJSON値そのままで、DOMのvalue属性は文字列しか持てない。indexで対応付ける。 */}
          <option value="">（未選択）</option>
          {(field.options ?? []).map((option, index) => (
            <option key={index} value={String(index)}>
              {option.label}
            </option>
          ))}
        </select>
      );
    case 'text':
      return (
        <input
          {...control}
          type="text"
          value={String(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

/**
 * widgetの生の値をschemaの型へ寄せたobjectにする。空欄（coerceFieldValueがundefined）のキーは入れない。
 * HumanTaskの回答と開始入力フォーム（StartRunPanel）の両方がこれで送る値を組む。
 */
export function buildFieldValues(
  fields: FormField[],
  inputs: Record<string, WidgetValue>,
  answerSchema: JsonObject,
): Record<string, unknown> {
  const answer: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = inputs[field.key] ?? (field.widget === 'checkbox' ? false : '');
    if (field.widget === 'select') {
      if (typeof raw !== 'string' || raw === '') continue;
      const option = field.options?.[Number(raw)];
      if (option !== undefined) answer[field.key] = option.value;
      continue;
    }
    const value = coerceFieldValue(raw, fieldType(answerSchema, field.key));
    if (value !== undefined) answer[field.key] = value;
  }
  return answer;
}

interface AnswerProblemNoticeProps {
  problem: Problem | null;
  expectedVersion: number;
  unkeyedMessages: string[];
  onReload(): void;
}

function AnswerProblemNotice({
  problem,
  expectedVersion,
  unkeyedMessages,
  onReload,
}: AnswerProblemNoticeProps): ReactElement | null {
  if (problem === null) return null;

  if (problem.status === 503) {
    // 配備中の一時的な拒否。失敗として赤く出さない。
    return (
      <div className="form-notice" role="status">
        <p>配備中のため一時的に受け付けていません。少し待って再送してください。</p>
        <p className="form-error-meta">{problemMetaText(problem)}</p>
      </div>
    );
  }

  if (problem.status === 409) {
    return (
      <div className="form-conflict" role="alert">
        <p>
          別の経路で回答済みの可能性があります。この画面は版 {expectedVersion} を前提にしています。
        </p>
        <button type="button" className="form-reload" onClick={onReload}>
          最新を取得
        </button>
        <p className="form-error-meta">{problemMetaText(problem)}</p>
      </div>
    );
  }

  if (problem.status === 422) {
    return (
      <div className="form-error" role="alert">
        <p>入力内容がサーバー検証で拒否されました。</p>
        {unkeyedMessages.map((message) => (
          <p className="form-error-detail" key={message}>
            {message}
          </p>
        ))}
        <p className="form-error-meta">{problemMetaText(problem)}</p>
      </div>
    );
  }

  return (
    <div className="form-error" role="alert">
      <p>{problem.title}</p>
      <p className="form-error-detail">{problem.detail}</p>
      <p className="form-error-meta">{problemMetaText(problem)}</p>
    </div>
  );
}

function ViewPanel({ view }: { view: JsonObject }): ReactElement | null {
  const entries = Object.entries(view);
  if (entries.length === 0) return null;
  return (
    <dl className="task-view">
      {entries.map(([key, value]) => (
        <div className="task-view-row" key={key}>
          <dt className="task-view-key">{key}</dt>
          <dd className="task-view-value">{displayViewValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

const ATTACHMENT_NOTICE = '（添付は表示しません）';

/**
 * viewは添付Base64を含まない公開projectionの想定だが、microapp側の実装次第で混入し得る。
 * data: URIか、256文字以上でbase64の文字種（英数字と + / = と空白）だけで構成された文字列を
 * 添付とみなし本文を描かない。日本語の説明文や短いIDを巻き込まないよう長さの下限を置いている。
 */
function maskAttachment(text: string): string {
  if (/^data:/i.test(text)) return ATTACHMENT_NOTICE;
  if (text.length >= 256 && /^[A-Za-z0-9+/=\s]+$/.test(text)) return ATTACHMENT_NOTICE;
  return text;
}

function displayViewValue(value: unknown): string {
  if (typeof value === 'string') return maskAttachment(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return '—';
  // 入れ子は整形より「隠さない」ことを優先しJSONで出す。文字列leafには同じmaskを掛ける。
  return JSON.stringify(value, (_key, nested: unknown) =>
    typeof nested === 'string' ? maskAttachment(nested) : nested,
  );
}

/**
 * Slack/Teamsへ出す表示の読み取り専用プレビュー。
 * 実操作はAdapterJWT専用の内部APIだけが受け付けるため、ここには押せるボタンを置かない。
 */
function ConversationPreview({
  conversation,
}: {
  conversation: ConversationPresentation;
}): ReactElement {
  const { message, actions, reply } = conversation;
  return (
    <section className="conversation-preview" aria-label="会話プレビュー">
      <p className="conversation-preview-note">
        Slack/Teamsではこう見えます（この画面からは操作できません）。
      </p>
      <p className="conversation-message">{message.text}</p>
      {message.mentions !== undefined && message.mentions.length > 0 && (
        <ul className="conversation-mentions">
          {message.mentions.map((mention) => (
            <li className="conversation-mention" key={`${mention.kind}:${mention.id}`}>
              {mention.label}
            </li>
          ))}
        </ul>
      )}
      <ul className="conversation-actions">
        {actions.map((action) => (
          <li className="conversation-action" key={`${action.key}:${action.choice ?? ''}`}>
            <span className="conversation-action-label">{action.label}</span>
            <span className="badge">{actorPolicyLabel(action.actorPolicy)}</span>
          </li>
        ))}
      </ul>
      <p className="conversation-reply">
        返信: {reply.enabled ? '受け付ける' : '受け付けない'} /{' '}
        {actorPolicyLabel(reply.actorPolicy)} /{' '}
        {reply.requireMention ? 'mention必須' : 'mention不要'}
      </p>
    </section>
  );
}

function actorPolicyLabel(policy: ConversationActorPolicy): string {
  return policy === 'requester' ? '申請者のみ' : '会話参加者';
}
